import React, { useRef } from "react";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { Trash2 } from "lucide-react";

interface LogoUploadProps {
  logoPreview: string;
  onLogoChange: (file: File) => void;
  onDrop: (file: File) => void;
  onRemove: () => void;
}

export const LogoUpload: React.FC<LogoUploadProps> = ({ logoPreview, onLogoChange, onDrop, onRemove }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { t } = useLanguage();

  const validateFile = (file: File): boolean => {
    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!validTypes.includes(file.type)) {
      toast({
        variant: "destructive",
        title: t("companyForm.toasts.invalidFormat.title"),
        description: t("companyForm.toasts.invalidFormat.description"),
      });
      return false;
    }

    if (file.size > maxSize) {
      toast({
        variant: "destructive",
        title: t("companyForm.toasts.fileTooLarge.title"),
        description: t("companyForm.toasts.fileTooLarge.description"),
      });
      return false;
    }

    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && validateFile(file)) {
      onLogoChange(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/") && validateFile(file)) {
      onDrop(file);
    }
  };

  return (
    <div className="space-y-4">
      <Label className="text-base font-medium">{t("companyForm.logo.label")}</Label>
      <div
        className="min-h-[400px] flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50 transition-colors relative"
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {logoPreview ? (
          <>
            <img
              src={logoPreview}
              alt={t("companyForm.logo.preview")}
              className="max-h-[360px] max-w-full object-contain"
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
              aria-label={t("companyForm.logo.remove")}
            >
              <Trash2 className="w-5 h-5 text-red-500" />
            </button>
          </>
        ) : (
          <div className="space-y-4">
            <div className="w-16 h-16 rounded-full bg-gray-50 border-2 border-dashed border-gray-300 flex items-center justify-center mx-auto">
              <span className="text-gray-400 text-2xl">+</span>
            </div>
            <div>
              <p className="text-base text-gray-500">
                {t("companyForm.logo.dragAndDrop")}{" "}
                <span className="text-teal-600 hover:text-teal-700 underline">{t("companyForm.logo.browse")}</span>
              </p>
              <p className="text-sm text-gray-400 mt-2">{t("companyForm.logo.supportedFormats")}</p>
            </div>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/jpg"
          onChange={handleFileChange}
          className="hidden"
          id="logo-upload"
          aria-label={t("companyForm.logo.input")}
        />
      </div>
    </div>
  );
};
